import {
  Accordion,
  AccordionItem,
  Loading,
  Modal,
} from "@appkit4/react-components";
import { useEffect, useMemo, useState } from "react";
import DataImportModal from "../../components/DataImportModal/DataImportModal";
import toast from "../../components/DismissibleToast";
import {
  Button,
  Input,
  InputNumber,
} from "../../components/ReadonlyAwareInputs";
import { getPageTooltips } from "../../services/GroupContext";
import { setEBITDATotals } from "../../services/calculations/GroupDataCalcs";
import { formatCurrency } from "../../utils/formatters";
import GroupDataTable from "./GroupDataTable";
import {
  WarningModal,
  handleCancel,
  renderPrimaryAccordionTitle,
  sumArray,
} from "./groupDataUtil";
import { getEbitdaRowConfig } from "./utils/ebitdaUtil";
import { useGroupDataRows } from "./utils/groupDataUtil";

const EBITDA = ({
  setVisible,
  currentPeriod,
  setCurrentPeriod,
  save,
  errorState: [isError, setIsError],
  isParentError,
  isReadOnly,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [ebitdaSubTotal, setEbitdaSubTotal] = useState(0);

  const tooltips = useMemo(() => getPageTooltips("ebitdaModal"), []);
  const ebitdaRowConfig = useMemo(
    () => getEbitdaRowConfig(tooltips),
    [tooltips],
  );
  const tables = useGroupDataRows(ebitdaRowConfig);

  const groupsProfitBeforeTax = [
    tables["group_profit_before_tax_s416_1"]?.state[0],
    tables["exclude_rd_credits_s104a_s416_2a"]?.state[0],
    tables["eliminate_derivative_fv_amounts_s420_3"]?.state[0],
    tables["include_replacement_derivative_amounts_s420_5"]?.state[0],
  ];

  const deprAmortCapExAdj = [
    tables["relevant_assets_depreciation_s417_2a"]?.state[0],
    tables["relevant_assets_amortization_s417_2a"]?.state[0],
    tables["relevant_assets_impairment_s417_2a"]?.state[0],
    tables["capital_expenditure_relevant_assets_s417_2b"]?.state[0],
    tables["provision_future_capital_expenditure_s417_2c"]?.state[0],
    tables["reversal_capital_expenditure_s417_1_3"]?.state[0],
    tables["capital_income_in_pbt_s417_1_4"]?.state[0],
    tables["capital_expenditure_adjustment_other"]?.state[0],
  ];

  const capFairValueMoveAdj = [tables["fair_value_movements_s418"]?.state[0]];

  const capDisposalsAdj = [
    tables["losses_disposals_relevant_assets_s419"]?.state[0],
    tables["profits_disposals_relevant_assets_s419"]?.state[0],
    tables["recalculated_profit_group_ebitda_s419_2_8"]?.state[0],
  ];

  const intAllowanceAltCalcAdj = [
    tables["remove_employer_pension_contributions_s424_2"]?.state[0],
    tables["include_uk_tax_employer_pensions_s424_3"]?.state[0],
    tables["remove_unpaid_remuneration_expenses_s424a_2"]?.state[0],
    tables["deduct_unpaid_remuneration_when_paid_s424a_3"]?.state[0],
    tables["remove_employee_share_acquisitions_s425_2"]?.state[0],
    tables["deductions_part11_part12_cta2009_s425_3"]?.state[0],
    tables["adjust_pbt_accounting_policy_changes_s426_2"]?.state[0],
  ];

  const intAllowanceNonConsAdj = [
    tables["exclude_income_financial_liabilities_s427_3a"]?.state[0],
    tables["exclude_interest_related_profit_loss_s427_3b"]?.state[0],
    tables["adjust_group_ebitda_associated_groups_s427_6"]?.state[0],
  ];

  const intAllowanceConsPartAdj = [
    tables["exclude_consolidated_partnerships_amounts_s430_2a"]?.state[0],
    tables["include_equity_method_partnerships_s430_2b"]?.state[0],
  ];

  const infraCoAdj = [
    tables["exclude_qualifying_infrastructure_companies_s442_3"]?.state[0],
  ];

  const otherAdj = [
    tables["specialist_regimes_other_adjustments"]?.state[0],
    tables["other_adjustments"]?.state[0],
  ];

  const [activeKeys, setActiveKeys] = useState("");
  const onClickAccordion = (activeKeys) => {
    setActiveKeys(activeKeys);
  };
  const [ngieETotal, setNgieTotal] = useState(
    currentPeriod?.local_curr_net_group_interest_expense
      ? currentPeriod?.local_curr_net_group_interest_expense
      : 0,
  );
  const calculateInterimEBITDA = () => {
    let total =
      Math.round(
        sumArray(groupsProfitBeforeTax) +
          sumArray(deprAmortCapExAdj) +
          sumArray(capFairValueMoveAdj) +
          sumArray(capDisposalsAdj) +
          sumArray(intAllowanceAltCalcAdj) +
          sumArray(intAllowanceConsPartAdj) +
          sumArray(intAllowanceNonConsAdj) +
          sumArray(infraCoAdj) +
          sumArray(otherAdj),
      ) + ngieETotal;
    return total;
  };

  useEffect(() => {
    let total =
      sumArray(groupsProfitBeforeTax) +
      sumArray(deprAmortCapExAdj) +
      sumArray(capFairValueMoveAdj) +
      sumArray(capDisposalsAdj) +
      sumArray(intAllowanceAltCalcAdj) +
      sumArray(intAllowanceConsPartAdj) +
      sumArray(intAllowanceNonConsAdj) +
      sumArray(infraCoAdj) +
      sumArray(otherAdj);
    setEbitdaSubTotal(total);
  }, [
    groupsProfitBeforeTax,
    deprAmortCapExAdj,
    capFairValueMoveAdj,
    capDisposalsAdj,
    intAllowanceAltCalcAdj,
    intAllowanceConsPartAdj,
    intAllowanceNonConsAdj,
    infraCoAdj,
    otherAdj,
  ]);

  const updateEbitdaValues = (ebitda) => {
    const newPeriod = {
      ...currentPeriod,
      ebitda,
    };

    setEBITDATotals(newPeriod);

    return newPeriod;
  };
  const submit = () => {
    const ebitda = Object.keys(tables)?.reduce((acc, k) => {
      acc[k] = tables[k]?.state[0];
      return acc;
    }, {});

    const newPeriod = updateEbitdaValues(ebitda);

    setCurrentPeriod(newPeriod);
    const promise = save(newPeriod);
    toast.promise(promise, {
      loading: "Saving Group EBITDA data...",
      success: () => {
        setVisible(false);
        return "Saved Group EBITDA data";
      },
      error: "Something went wrong with saving Group EBITDA data",
    });
  };

  useEffect(
    () => {
      setIsError(Object.keys(tables)?.some((k) => tables[k]?.error[0]));
    },
    Object.keys(tables)?.map((k) => tables[k]?.error[0]),
  );

  useEffect(() => {
    if (!currentPeriod) {
      return;
    }

    const ebitda = currentPeriod?.ebitda;
    if (!ebitda) {
      return;
    }

    Object.keys(tables)?.forEach((key) => {
      tables[key]?.state[1](ebitda[key]);
    });
  }, [currentPeriod]);

  const [isModalChanged, setIsModalChanged] = useState(false);

  useEffect(() => {
    let currTotal = calculateInterimEBITDA();
    if (currentPeriod?.angie_amount_before_any_adjustments) {
      currTotal += currentPeriod?.angie_amount_before_any_adjustments;
    }
    if (currTotal > 0 && currentPeriod?.local_curr_group_ebitda !== currTotal) {
      setIsModalChanged(true);
    } else {
      setIsModalChanged(false);
    }
  }, [calculateInterimEBITDA()]);

  const onImportSuccess = (ebitda) => {
    const isPlainObject = (val) =>
      typeof val === "object" && val !== null && val.constructor === Object;

    if (isPlainObject(ebitda)) {
      updateEbitdaValues(ebitda);
      Object.entries(tables)?.forEach(([key, row]) => {
        row.state[1](ebitda[key]);
      });
    } else {
      console.error("error setting values");
      toast.error("Error Importing QNGIE");
    }
  };

  const handleExitWithoutSaving = () => {
    setIsError(false);
    setVisible(false);
  };

  if (isLoading) {
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );
  }

  return (
    <>
      <Modal
        initialFocus={false}
        visible={true}
        title={`Group EBITDA`}
        onCancel={() =>
          handleCancel(
            isModalChanged,
            isReadOnly,
            setVisible,
            setShowWarningModal,
            setIsError,
          )
        }
        maskCloseable={false}
        modalStyle={{ width: "90%", maxHeight: "90%" }}
        bodyStyle={{ maxHeight: "90%" }}
      >
        <table className="table">
          <tbody>
            <tr>
              <td>
                <div className="row">
                  <p className="col-1">Selected input value currency:</p>
                  <Input
                    className="col"
                    disabled
                    value={currentPeriod?.currency}
                  />
                </div>
              </td>
              <td>
                <div className="row">
                  <p className="col-4">
                    Entered FX rate: 1 GBP = X Local Currency (if required):
                  </p>
                  <InputNumber
                    className="col"
                    value={currentPeriod?.fx_rate}
                    disabled
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="row group-data-modal-top-input-header">
          <p>
            Changes to the currency and exchange rate can be made iFn the Net
            group-interest expense section
          </p>
        </div>

        {isError && (
          <div className="err">
            Description missing - a description must be provided for every
            value.
          </div>
        )}

        <Accordion
          multiple={false}
          onClick={onClickAccordion}
          activeKeys={activeKeys}
        >
          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Group's Profit Before Tax from P&L",
              sumArray(groupsProfitBeforeTax),
            )}
            itemKey="1"
          >
            {GroupDataTable(tables, [
              "group_profit_before_tax_s416_1",
              "exclude_rd_credits_s104a_s416_2a",
              "eliminate_derivative_fv_amounts_s420_3",
              "include_replacement_derivative_amounts_s420_5",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "NGIE",
              Math.round(ngieETotal),
              tooltips.NGIE,
            )}
            itemKey="2"
          ></AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Depreciation and Amortisation adjustment - Capital Expenditure Adjustment (s417)",
              sumArray(deprAmortCapExAdj),
            )}
            itemKey="3"
          >
            {GroupDataTable(tables, [
              "relevant_assets_depreciation_s417_2a",
              "relevant_assets_amortization_s417_2a",
              "relevant_assets_impairment_s417_2a",
              "capital_expenditure_relevant_assets_s417_2b",
              "provision_future_capital_expenditure_s417_2c",
              "reversal_capital_expenditure_s417_1_3",
              "capital_income_in_pbt_s417_1_4",
              "capital_expenditure_adjustment_other",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Capital fair value movement adjustment (s418):",
              sumArray(capFairValueMoveAdj),
            )}
            itemKey="4"
          >
            {GroupDataTable(tables, ["fair_value_movements_s418"])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Capital disposals adjustment (s419):",
              sumArray(capDisposalsAdj),
            )}
            itemKey="5"
          >
            {GroupDataTable(tables, [
              "losses_disposals_relevant_assets_s419",
              "profits_disposals_relevant_assets_s419",
              "recalculated_profit_group_ebitda_s419_2_8",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Adjustments in respect of the interest allowance (alternative calculation) election (s423-426):",
              sumArray(intAllowanceAltCalcAdj),
            )}
            itemKey="6"
          >
            {GroupDataTable(tables, [
              "remove_employer_pension_contributions_s424_2",
              "include_uk_tax_employer_pensions_s424_3",
              "remove_unpaid_remuneration_expenses_s424a_2",
              "deduct_unpaid_remuneration_when_paid_s424a_3",
              "remove_employee_share_acquisitions_s425_2",
              "deductions_part11_part12_cta2009_s425_3",
              "adjust_pbt_accounting_policy_changes_s426_2",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Adjustments in respect of the interest allowance (non-consolidated investment) election",
              sumArray(intAllowanceNonConsAdj),
            )}
            itemKey="7"
          >
            {GroupDataTable(tables, [
              "exclude_income_financial_liabilities_s427_3a",
              "exclude_interest_related_profit_loss_s427_3b",
              "adjust_group_ebitda_associated_groups_s427_6",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Adjustments in respect of the interest allowance (consolidated partnership) election",
              sumArray(intAllowanceConsPartAdj),
            )}
            itemKey="8"
          >
            {GroupDataTable(tables, [
              "exclude_consolidated_partnerships_amounts_s430_2a",
              "include_equity_method_partnerships_s430_2b",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Adjustments in respect of qualifying infrastructure companies",
              sumArray(infraCoAdj),
            )}
            itemKey="9"
          >
            {GroupDataTable(tables, [
              "exclude_qualifying_infrastructure_companies_s442_3",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle("Other", sumArray(otherAdj))}
            itemKey="10"
          >
            {GroupDataTable(tables, [
              "specialist_regimes_other_adjustments",
              "other_adjustments",
            ])}
          </AccordionItem>
        </Accordion>

        <h3>Summary</h3>
        {currentPeriod?.currency == "GBP" ? (
          <>
            <h4>
              Overall Total:{" "}
              <span className="red-text">
                {formatCurrency(Math.round(calculateInterimEBITDA()))} GBP
              </span>
            </h4>
          </>
        ) : (
          <>
            <h4>
              FX rate: 1 (GBP) ={" "}
              <span className="red-text">
                {formatCurrency(currentPeriod?.fx_rate)}{" "}
                {currentPeriod?.currency}
              </span>
            </h4>
            <h4>
              Overall Total:{" "}
              <span className="red-text">
                {formatCurrency(Math.round(calculateInterimEBITDA()))}{" "}
                {currentPeriod?.currency}
              </span>
            </h4>
            <h4>
              Overall Total:{" "}
              <span className="red-text">
                {formatCurrency(
                  Math.round(calculateInterimEBITDA() / currentPeriod?.fx_rate),
                )}{" "}
                GBP
              </span>
            </h4>
          </>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
          }}
        >
          <DataImportModal pageName="ebitda" onSuccess={onImportSuccess} />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
              paddingTop: "30px",
              gap: 10,
            }}
          >
            <Button
              neverReadonly
              onClick={() =>
                handleCancel(
                  isModalChanged,
                  isReadOnly,
                  setVisible,
                  setShowWarningModal,
                  setIsError,
                )
              }
              kind="secondary"
            >
              Cancel
            </Button>
            <Button
              hiddenInReadonly
              disabled={isError || isParentError}
              onClick={submit}
            >
              Submit
            </Button>
          </div>
        </div>
      </Modal>

      <WarningModal
        visible={showWarningModal}
        setIsVisible={setShowWarningModal}
        callBackFunc={handleExitWithoutSaving}
      ></WarningModal>
    </>
  );
};

export default EBITDA;
